import * as React from 'react';

import { Popover } from '@mui/material';

import { OnboardingHealthStatus } from '@sbv-clients/data-access-api';
import {
  Badge,
  Box,
  CustomBadge,
  Typography,
  theme,
} from '@sbv-clients/ui-shared';

import { AdminFacilityElasticEmployeeType } from '../../../services/admin-facility';
import { OnboardingHealthStatusConst } from './onboarding-health-status-const';

interface ProblemBadgeProps {
  text: string;
  type?: 'error' | 'info' | 'success' | 'warning';
  fullWidth?: boolean;
  textAlign?: 'left' | 'center' | 'right';
}

const ProblemBadge: React.FC<ProblemBadgeProps> = ({
  text,
  fullWidth = false,
  textAlign = 'center',
  type = 'error',
}) => {
  return (
    <Badge
      sx={{
        margin: '0 2px',
        width: fullWidth ? '100%' : 'auto',
        textAlign,
      }}
      color={type}
    >
      <CustomBadge sx={{ width: '100%' }} type={type}>
        <Typography fontSize="small">{text}</Typography>
      </CustomBadge>
    </Badge>
  );
};

const paperStyles = { p: 2, maxWidth: 600, maxHeight: 400, overflowY: 'auto' };

export function EmployeeHealthProblemBadgeCollection({
  onboardingHealthStatusList,
  duplicateLoginIdEmployees,
  duplicateMobilePhoneEmployees,
  linkedEmployees,
}: {
  onboardingHealthStatusList: OnboardingHealthStatus[];
  duplicateLoginIdEmployees?: AdminFacilityElasticEmployeeType[];
  duplicateMobilePhoneEmployees?: AdminFacilityElasticEmployeeType[];
  linkedEmployees?: AdminFacilityElasticEmployeeType[];
}) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [popoverType, setPopoverType] = React.useState<
    'phone' | 'login' | 'linked' | null
  >(null);

  const handleOpen = (
    event: React.MouseEvent<HTMLElement>,
    type: 'phone' | 'login' | 'linked'
  ) => {
    setAnchorEl(event.currentTarget);
    setPopoverType(type);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPopoverType(null);
  };

  const open = Boolean(anchorEl);

  const renderEmployeeList = (
    employees: AdminFacilityElasticEmployeeType[]
  ) => (
    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr>
          {['DB', 'Name', 'Employee ID', 'Mobile Phone', 'Login ID'].map(
            (header) => (
              <th
                key={header}
                style={{
                  borderBottom: `1px solid ${theme.colors.border.neutral}`,
                  padding: '4px',
                }}
              >
                {header}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {employees.map((emp) => (
          <tr key={emp.pkEmployeeId}>
            <td style={{ padding: '4px' }}>{emp.databaseName}</td>
            <td
              style={{ padding: '4px' }}
            >{`${emp.lastName}, ${emp.firstName}`}</td>
            <td style={{ padding: '4px' }}>{emp.employeeId}</td>
            <td style={{ padding: '4px' }}>{emp.mobilePhone}</td>
            <td style={{ padding: '4px' }}>{emp.legacyLoginId}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );

  const badges: JSX.Element[] = [];

  const renderPopoverContent = (type: 'phone' | 'login' | 'linked' | null) => {
    switch (type) {
      case 'phone':
        return renderEmployeeList(duplicateMobilePhoneEmployees ?? []);
      case 'login':
        return renderEmployeeList(duplicateLoginIdEmployees ?? []);
      case 'linked':
        return renderEmployeeList(linkedEmployees ?? []);
      default:
        return null;
    }
  };
  const isReady = onboardingHealthStatusList.includes(
    OnboardingHealthStatusConst.READY.value
  );

  if (isReady) {
    badges.push(<ProblemBadge key="ready" text="Ready" type="success" />);
  }

  if (
    (duplicateMobilePhoneEmployees?.length || 0) > 0 &&
    onboardingHealthStatusList.includes(
      OnboardingHealthStatusConst.DUPLICATE_PHONE.value
    )
  ) {
    badges.push(
      <Box
        key="popover-phone"
        component="span"
        onClick={(e) => handleOpen(e, 'phone')}
        sx={{ cursor: 'pointer', display: 'inline-block' }}
      >
        <ProblemBadge text="Duplicate Phone" type="warning" />
      </Box>
    );
  }

  if (
    (duplicateLoginIdEmployees?.length || 0) > 0 &&
    onboardingHealthStatusList.includes(
      OnboardingHealthStatusConst.DUPLICATE_LOGIN.value
    )
  ) {
    badges.push(
      <Box
        key="popover-login"
        component="span"
        onClick={(e) => handleOpen(e, 'login')}
        sx={{ cursor: 'pointer', display: 'inline-block' }}
      >
        <ProblemBadge text="Duplicate Login Id" type="warning" />
      </Box>
    );
  }
  if (
    (linkedEmployees?.length || 0) > 0 &&
    onboardingHealthStatusList.includes(
      OnboardingHealthStatusConst.OTHER_MATCHES.value
    )
  ) {
    badges.push(
      <Box
        key="popover-linked-employees"
        component="span"
        onClick={(e) => handleOpen(e, 'linked')}
        sx={{ cursor: 'pointer', display: 'inline-block' }}
      >
        <ProblemBadge text="Other Matches" type="success" />
      </Box>
    );
  }

  if (
    onboardingHealthStatusList.includes(
      OnboardingHealthStatusConst.INVALID_MOBILE_PHONE.value
    )
  ) {
    badges.push(
      <ProblemBadge
        key="invalid-mobile"
        text="Invalid Mobile Phone"
        type="error"
      />
    );
  }

  if (badges.length === 0) return <></>;

  return (
    <>
      <Box>{badges}</Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: paperStyles,
          },
        }}
      >
        {renderPopoverContent(popoverType)}
      </Popover>
    </>
  );
}
